import { z } from 'zod';
import { adPlatformSchema } from './ad-platform.js';
import { googleKeywordTextSchema } from './google-keyword.js';
// [12.2L-120] mag-strategist agent output shape.
//
// ONE campaign per proposal (portfolio emerges over weeks via repeat
// strategist runs). The campaign can span multiple platforms via the
// `channels[]` array — same campaign idea expressed as a Google Search
// component + a Meta component, etc.
//
// Shape constraints:
//   - cap on field sizes so the agent can't blow the token budget
//   - bounded enums where possible so validators don't have to do
//     fuzzy matching
//   - all currency amounts in USD; conversion to brand-tz currency
//     happens at display time
/** Match type for keyword targeting. Same as Google's KeywordMatchType
 *  with the BROAD MATCH MODIFIED variant collapsed to BROAD because
 *  Google deprecated it. */
export const keywordMatchTypeSchema = z.enum(['EXACT', 'PHRASE', 'BROAD']);
/** Competition level. Mirrors the Competition type in the research
 *  client so a strategist proposal's claimed competition is directly
 *  comparable to KP's reported value. */
export const strategistCompetitionSchema = z.enum(['low', 'medium', 'high', 'unknown']);
/** One keyword the proposal targets, with the data point that backs
 *  the choice. The strategist agent MUST cite real research data here
 *  — the validators refuse keywords without backing. */
export const strategistKeywordSchema = z.object({
    /** Both Google's limits, plus this surface's own floor.
     *
     *  The floor was here before the shared schema and is KEPT: a one- or
     *  two-character keyword is something Google would accept and no researched
     *  proposal should contain. Expressed as a refinement because
     *  googleKeywordTextSchema already carries one, so `.min(3)` is no longer
     *  available on it — the alternative, respelling both limits here to get
     *  `.min(3)` back, is the drift this change exists to remove. */
    text: googleKeywordTextSchema.refine((t) => t.trim().length >= 3, {
        message: 'keyword must be at least 3 characters',
    }),
    matchType: keywordMatchTypeSchema,
    /** Monthly search volume midpoint from the research client. Code-
     *  verified against the cached research-client output (validator
     *  refuses fabricated numbers). */
    monthlySearchVolumeMidpoint: z.number().int().min(0),
    /** Competition signal from KP / equivalent. */
    competition: strategistCompetitionSchema,
});
/** One headline on a responsive search ad. Pinned field is optional
 *  and respects Google's three-slot constraint. */
export const strategistHeadlineSchema = z.object({
    text: z.string().min(1).max(30),
    pinnedField: z.enum(['HEADLINE_1', 'HEADLINE_2', 'HEADLINE_3']).optional(),
});
export const strategistDescriptionSchema = z.object({
    text: z.string().min(1).max(90),
});
/** [12.2L-135] Meta audience targeting primitive. Interests +
 *  behaviors + demographic constraints + optional lookalike seed.
 *  Used by Meta channels in place of keywords. */
export const strategistMetaInterestSchema = z.object({
    /** Founder-facing interest label. Mag emits the canonical Meta
     *  interest name when available. */
    label: z.string().min(2).max(100),
    /** Meta's targeting-search id when known. Optional — when null,
     *  the meta research client resolves the id at launch time. */
    metaId: z.string().min(1).max(40).optional(),
    /** Estimated reach in millions, from Meta's targeting estimate. */
    estimatedReachMillions: z.number().min(0.01).max(2_000).optional(),
});
export const strategistMetaAudienceSchema = z.object({
    /** Country ISO (uppercase). One-element today; multi-country in v2. */
    countries: z.array(z.string().length(2)).min(1).max(8),
    /** Age range — Meta accepts 13-65+ but most ads want narrower. */
    ageMin: z.number().int().min(13).max(65),
    ageMax: z.number().int().min(13).max(65),
    /** Optional gender filter. Omit for all. */
    genders: z
        .array(z.enum(['male', 'female', 'all']))
        .max(3)
        .optional(),
    interests: z.array(strategistMetaInterestSchema).min(1).max(20),
    /** Mag's read of "why this audience": one sentence. */
    rationale: z.string().min(10).max(400),
});
/** Meta ad creative — image / video carousel placeholder. Phase 1
 *  ships with image URLs from brand assets; actual creative
 *  generation pipeline lives in @magig/creative and is wired into
 *  the Meta launch path when that arc lands. */
export const strategistMetaCreativeSchema = z.object({
    type: z.literal('meta_image'),
    /** Brand asset URL the agent picked. */
    assetUrl: z.string().url().max(2000),
    primaryText: z.string().min(1).max(220),
    headline: z.string().min(1).max(60),
    description: z.string().min(1).max(60).optional(),
    callToAction: z
        .enum([
        'LEARN_MORE',
        'SHOP_NOW',
        'SIGN_UP',
        'GET_QUOTE',
        'CONTACT_US',
        'DOWNLOAD',
        'BOOK_TRAVEL',
    ])
        .default('LEARN_MORE'),
    destinationUrl: z.string().url().max(2000),
});
/** Google Search channel — keywords + RSAs. The original shape. */
export const strategistGoogleChannelSchema = z.object({
    platform: z.literal('google'),
    campaignName: z.string().min(3).max(120),
    dailyBudgetUsd: z.number().int().min(1).max(1000),
    keywords: z.array(strategistKeywordSchema).min(1).max(30),
    /** RSAs for the ad group. Spec `two-rsas-per-ad-group`.
     *
     *  TWO IS THE FLOOR, not a preference. Google recommends at least 2
     *  responsive search ads per ad group and publishes the lift — +6.6%
     *  conversions for the second at a similar cost per conversion, +3.7% for
     *  the third (theory 81edf7e). A single-ad group cannot rotate, and mag's
     *  own takeover review flags one as a red flag, so emitting one here would
     *  ship the exact defect she reports to founders.
     *
     *  `max(3)` was already google's ceiling of 3 enabled RSAs and is unchanged;
     *  only the floor moved. `launchAiStrategistCampaign` sends creatives[0] as
     *  the ad group's ad and `slice(1)` as extra RSAs in the SAME ad group, so a
     *  one-creative channel silently produced an unrotatable group.
     *
     *  The floor is a hard type here, unlike the deliverable's optional
     *  `extra_creatives`, because strategy proposals are not re-validated from
     *  stored history — nothing in production breaks by tightening it (RES-2). */
    creatives: z
        .array(z.object({
        type: z.literal('rsa'),
        headlines: z.array(strategistHeadlineSchema).min(3).max(15),
        descriptions: z.array(strategistDescriptionSchema).min(2).max(4),
        finalUrl: z.string().url().max(2000),
    }))
        .min(2)
        .max(3),
    /** Negatives attached after creation, via the ad-group negative-keyword
     *  mutate (strategy-to-google-ads.ts). A keyword by every name except the
     *  field's — which is why it was the one site the `matchType`-adjacency
     *  guard could not see, and why that guard now also reads field NAMES.
     *
     *  The ceiling moves 60 -> 80 with the shared schema: 80 is Google's actual
     *  limit, so the old number refused terms Google accepts. The min(2) floor
     *  is kept — a one-character negative excludes nothing anybody searched. */
    extraNegatives: z
        .array(googleKeywordTextSchema.refine((t) => t.trim().length >= 2))
        .max(20)
        .default([]),
});
/** [12.2L-165] M14 — Meta channel kind discriminator.
 *
 *  cold_traffic: mag designs fresh creatives via the M13 bridge and
 *    targets cold interest-based audiences. The default shape — what
 *    the bridge has produced since M13.1.
 *  promote_post: mag picks an already-published organic IG post that's
 *    outperforming the brand's baseline and asks for paid spend behind
 *    it. The "boost this post" agency move. Launch path uses Meta's
 *    existing-post boost endpoint; no fresh render needed.
 *
 *  Both shapes share audience + budget — only the creative source
 *  differs. The bridge routes on this discriminator. */
export const strategistMetaChannelKindSchema = z.enum(['cold_traffic', 'promote_post']);
/** Meta channel — audience + image creatives. The `kind` discriminator
 *  routes the bridge: cold_traffic renders fresh PNGs; promote_post
 *  boosts an existing organic IG post. Default is cold_traffic so
 *  proposals persisted pre-M14 parse without re-migration. */
export const strategistMetaChannelSchema = z
    .object({
    platform: z.literal('meta'),
    kind: strategistMetaChannelKindSchema.default('cold_traffic'),
    campaignName: z.string().min(3).max(120),
    dailyBudgetUsd: z.number().int().min(1).max(1000),
    audience: strategistMetaAudienceSchema,
    creatives: z.array(strategistMetaCreativeSchema).min(1).max(3),
    /** When kind='promote_post', the IG media id of the post being
     *  boosted. Required for promote_post; ignored on cold_traffic. */
    sourcePostId: z.string().min(1).max(64).optional(),
    /** When kind='promote_post', the engagement-vs-baseline ratio
     *  the agent cited as justification (e.g. 2.1 = 2.1× baseline).
     *  Reviewer/validator can cross-check against the candidate set
     *  the strategist context block surfaced. */
    sourcePostEngagementRatio: z.number().min(0).max(100).optional(),
    /** [12.2L-165] Card-rendering breadcrumbs stamped by run-strategist
     *  after the candidate match (NOT emitted by the agent). Optional
     *  because cold_traffic channels don't carry them. */
    sourcePostPermalink: z.string().url().max(500).optional(),
    sourcePostCaptionExcerpt: z.string().max(280).optional(),
})
    .refine((c) => c.kind !== 'promote_post' ||
    (typeof c.sourcePostId === 'string' && c.sourcePostId.length > 0), { message: 'promote_post Meta channel must carry sourcePostId' });
/** [12.2L-135] Per-platform component of a strategy proposal.
 *  Discriminated by `platform` — google channels are keyword-shaped,
 *  meta channels are audience-shaped. The strategist agent emits
 *  the discriminated form; accept / launch / cool-down branch on
 *  the discriminator. */
export const strategistChannelSchema = z.discriminatedUnion('platform', [
    strategistGoogleChannelSchema,
    strategistMetaChannelSchema,
]);
/** Helper — pulls the keyword set when present, empty list for
 *  channels without keywords (Meta audience channels). Used by
 *  cool-down + cluster fingerprinting which were keyword-only until
 *  Meta channels landed. */
export function channelKeywordTexts(channel) {
    return channel.platform === 'google' ? channel.keywords.map((k) => k.text) : [];
}
/** Expected outcome metrics. Used by the hypothesis-vs-math validator
 *  to refuse proposals whose budget and outcome don't square. */
export const strategistExpectedOutcomeSchema = z.object({
    /** Mag's estimated CPA in USD. Validator: cpaUsdEstimate *
     *  conversionsPerMonthEstimate should be within 50% of
     *  monthlyBudgetUsd. */
    cpaUsdEstimate: z.number().min(1).max(10_000),
    conversionsPerMonthEstimate: z.number().int().min(1).max(100_000),
    /** Mag's own confidence in the estimate. Phase 5 uses this to
     *  weight learnings. */
    confidence: z.enum(['low', 'medium', 'high']),
});
export const strategyProposalSchema = z.object({
    /** Short founder-facing name. Lowercase mag voice. */
    name: z.string().min(3).max(120),
    /** 2-3 sentence hypothesis explaining the strategic call. Mag voice.
     *  Cites data points from the research client output. */
    hypothesis: z.string().min(20).max(800),
    /** One-paragraph rationale referencing specific cluster + brain
     *  observations. Logged but not surfaced primarily; founder reads
     *  the hypothesis. */
    rationale: z.string().min(20).max(1000),
    expectedOutcome: strategistExpectedOutcomeSchema,
    /** Sum of channels[].dailyBudgetUsd × 30. Surfaced as the envelope
     *  the founder sees; validator refuses mismatch with channel sum. */
    monthlyBudgetUsd: z.number().int().min(30).max(30_000),
    /** Which clusters from the research pipeline this proposal targets.
     *  Drives the cluster_fingerprint for the cool-down lookup + the
     *  active-campaign dedup check (when [12.2L-119] lands). */
    sourceClusterIds: z.array(z.number().int().min(0)).min(1).max(8),
    /** Per-platform components of the campaign. v1 typically has one
     *  element (Google); multi-channel adds Meta etc. */
    channels: z.array(strategistChannelSchema).min(1).max(3),
    /** Proposed start date, YYYY-MM-DD in brand-tz. Validator refuses
     *  past dates and dates >90d out. */
    proposedStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** SERVER-SET, never emitted by the agent. Why this proposal may not
     *  autolaunch, or absent when it may.
     *
     *  A proposal is not autonomous spend — it is a card the founder must
     *  accept — so exceeding mag's mistake budget, or being too small to
     *  ever reach a verdict, must not SUPPRESS it. Both used to: the
     *  strategist bailed with no_budget_headroom before the agent ran, and
     *  the budget validator refused an over-cap envelope outright. The
     *  founder saw nothing either way, and a brand with no spend history
     *  could never acquire the history that would raise its cap.
     *
     *  So the proposal is made, and this records what the founder's click
     *  is needed for. Optional because a proposal mag may launch herself
     *  carries no reasons at all. */
    autonomy: z
        .object({
        requiresFounderLaunch: z.literal(true),
        /** Founder-facing, one line each. */
        reasons: z.array(z.string().min(1).max(300)).min(1).max(4),
    })
        .optional(),
});
/** Top-level strategist agent output. The agent can decide there's
 *  nothing worth proposing (data is too thin, no clear wedge) and
 *  return decision: 'no_signal' instead of a proposal. */
export const strategistAgentOutputSchema = z.discriminatedUnion('decision', [
    z.object({
        decision: z.literal('propose'),
        proposal: strategyProposalSchema,
    }),
    z.object({
        decision: z.literal('no_signal'),
        /** One sentence mag-voice explanation. Surfaces nowhere by default;
         *  logged for diagnostics. */
        reason: z.string().min(10).max(280),
    }),
]);
//# sourceMappingURL=mag-strategist.js.map